<template>
  <div id="app">
    <!-- Tauri drag region - bar at top for window dragging (hidden for present/wled routes) -->
    <div 
      v-if="isDesktop && showTitlebar" 
      class="tauri-titlebar"
      @mousedown="startDrag"
      @dblclick="toggleMaximize"
    ></div>
    <RouterView />
    <Toaster />
    <WhatsNewDialog />
  </div>
</template>

<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { Toaster } from '@/components/ui/toast'
import { toast } from '@/components/ui/toast'
import WhatsNewDialog from '@/components/WhatsNewDialog.vue'
import { watch, onMounted, ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useScenesStore } from '@/stores/scenes'
import { useSceneDisplayConfigsStore } from '@/stores/sceneDisplayConfigs'
import { useDisplayLayersStore } from '@/stores/displayLayers'
import { useImageLibraryStore } from '@/stores/imageLibrary'
import { useSpotifyStore } from '@/stores/spotify'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'
import { useWledDevicesStore } from '@/stores/wledDevices'
import { useLightsStore } from '@/stores/lights'
import { logger } from '@/lib/logger'
import { Platform } from '@/lib/platform'
import { useAppUpdater } from '@/composables/useAppUpdater'

const route = useRoute()
const isDesktop = Platform.isDesktop()

// Don't show titlebar on present views or external URLs (like WLED interfaces)
const showTitlebar = computed(() => {
  const path = route.path
  // Hide titlebar for present views and any non-app routes
  return !path.startsWith('/present')
})

console.log('🖥️ [App] Platform detection:', {
  isDesktop,
  platform: Platform.getPlatformName(),
  hasTauri: typeof (window as any).__TAURI__ !== 'undefined',
  hasTauriInternals: typeof (window as any).__TAURI_INTERNALS__ !== 'undefined'
})

// Tauri window drag handler
const startDrag = async () => {
  if (!isDesktop) return
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window')
    const window = getCurrentWindow()
    await window.startDragging()
  } catch (err) {
    console.error('Failed to start window drag:', err)
  }
}

// Tauri window maximize/restore on double-click
const toggleMaximize = async () => {
  if (!isDesktop) return
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window')
    const window = getCurrentWindow()
    const isMaximized = await window.isMaximized()
    if (isMaximized) {
      await window.unmaximize()
    } else {
      await window.maximize()
    }
  } catch (err) {
    console.error('Failed to toggle maximize:', err)
  }
}

const authStore = useAuthStore()
const scenesStore = useScenesStore()
const configsStore = useSceneDisplayConfigsStore()
const layersStore = useDisplayLayersStore()
const imageLibraryStore = useImageLibraryStore()
const spotifyStore = useSpotifyStore()
const sceneLightConfigsStore = useSceneLightConfigsStore()
const wledDevicesStore = useWledDevicesStore()
const lightsStore = useLightsStore()

// App updater (desktop only)
const { updateStatus, checkForUpdates, downloadAndInstall } = useAppUpdater()

// Track if update is being installed
const isUpdating = ref(false)

// Check for updates on desktop app startup
onMounted(async () => {
  if (isDesktop) {
    // Wait a bit for app to fully load
    await new Promise(resolve => setTimeout(resolve, 3000))
    
    try {
      const hasUpdate = await checkForUpdates()
      
      if (hasUpdate && updateStatus.value.version) {
        toast({
          title: 'Update Available',
          description: `Version ${updateStatus.value.version} is ready. Restart the app to install.`,
          duration: 10000
        })
      }
    } catch (err) {
      logger.error('Failed to check for updates:', err)
    }
  }
})

// Track if stores have been initialized to prevent duplicate initialization
let storesInitialized = false
let initializationPromise: Promise<void> | null = null

// Initialize stores when user is authenticated
watch(() => authStore.user, async (user) => {
  if (user) {
    // Prevent duplicate initialization
    if (storesInitialized) {
      logger.debug('🔄 [App] Stores already initialized, skipping...')
      return
    }
    if (initializationPromise) {
      logger.debug('🔄 [App] Initialization in progress, waiting...')
      await initializationPromise
      return
    }
    
    logger.info('🚀 [App] User authenticated, initializing stores...')
    
    initializationPromise = (async () => {
      try {
        await Promise.all([
          scenesStore.fetchUserScenes(), // Also initializes realtime subscription
          configsStore.fetchAllConfigs(),
          layersStore.fetchAllLayers(),
          imageLibraryStore.fetchImages(),
          spotifyStore.initialize(), // Initialize Spotify on app load
          sceneLightConfigsStore.fetchConfigs(), // Fetch light configs for inheritance
          wledDevicesStore.fetchDevices(), // Fetch WLED devices
          lightsStore.fetchLights() // Fetch lights (including WLED)
        ])
        
        // Setup realtime subscriptions for other stores
        configsStore.subscribeToConfigUpdates()
        layersStore.subscribeToLayerUpdates()
        
        storesInitialized = true
        logger.info('✅ [App] All stores initialized and subscriptions active')
      } catch (err) {
        logger.error('❌ [App] Failed to initialize stores:', err)
      } finally {
        initializationPromise = null
      }
    })()
    
    await initializationPromise
  } else {
    // User logged out - cleanup subscriptions and reset flag
    storesInitialized = false
    initializationPromise = null
    scenesStore.cleanupRealtimeSubscription()
  }
}, { immediate: true })
</script>